/**
 * MOU — the stage between a won deal and an onboarded client.
 *
 * A chase list, not a table of documents. Its rows are won deals, and the ones
 * nobody has drafted an MOU for are the whole point: they arrive as
 * "Not yet prepared" from the backend, which stitches the `mou` table together
 * with every lead at a sales-completed stage.
 *
 * The default tab is "Needs action" for the same reason — landing on All would
 * open on a wall of signed clients that need nothing from anyone.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select } from "antd";
import {
  BadgeCheck, BarChart3, ChevronRight, Clock, Download, FileSignature, FileText, Hourglass,
  RefreshCw, Search, Send, Settings, ShieldAlert, UserPlus,
} from "lucide-react";

import apiClient from "../../lib/apiClient";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { MOU_EXPORT_URL, MOU_URL, USERS_SIMPLE_URL } from "../../constants/api";
import PrepareMOUModal from "./PrepareMOUModal";

// ── status presentation ──────────────────────────────────────────────────────
// One place, so the tabs, the badges and the empty states can never disagree
// about what a status is called.
export const MOU_STATUS_META = {
  not_prepared: {
    label: "Not yet prepared",
    badge: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
    icon: FileText,
  },
  draft: {
    label: "Prepared",
    badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    icon: FileSignature,
  },
  sent: {
    label: "Sent",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    icon: Send,
  },
  signed: {
    label: "MOU signed",
    badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    icon: BadgeCheck,
  },
  declined: {
    label: "Declined",
    badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    icon: ShieldAlert,
  },
  cancelled: {
    label: "Cancelled",
    badge: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
    icon: ShieldAlert,
  },
  expired: {
    label: "Expired",
    badge: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    icon: Hourglass,
  },
};

const TABS = [
  { key: "open",         label: "Needs action" },
  { key: "not_prepared", label: "Not yet prepared" },
  { key: "draft",        label: "Prepared" },
  { key: "sent",         label: "Sent" },
  { key: "signed",       label: "Signed" },
  { key: "declined",     label: "Declined" },
  { key: "expired",      label: "Expired" },
  { key: "all",          label: "All" },
];

export const fmtCurrency = (val) => {
  const n = parseFloat(val);
  return Number.isFinite(n) && n !== 0 ? `₹${n.toLocaleString("en-IN")}` : "—";
};

export const fmtDate = (val) =>
  val
    ? new Date(val).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "—";

export const StatusBadge = ({ status, label }) => {
  const meta = MOU_STATUS_META[status] || MOU_STATUS_META.not_prepared;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.badge}`}>
      <Icon className="h-3 w-3" />
      {label || meta.label}
    </span>
  );
};

// ── component ────────────────────────────────────────────────────────────────
export default function MOUList() {
  const { fetch } = useApiMethods();
  const navigate = useNavigate();

  const [rows, setRows]       = useState([]);
  const [summary, setSummary] = useState({});
  const [config, setConfig]   = useState({});
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);

  const [tab, setTab]         = useState("open");
  const [search, setSearch]   = useState("");
  const [owner, setOwner]     = useState("");
  const [prepareFor, setPrepareFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(MOU_URL, { status: tab, search, owner })
      .then((res) => {
        if (res?.status === "success") {
          setRows(res.data || []);
          setSummary(res.summary || {});
          setConfig(res.config || {});
        } else {
          ToastMsg("error", res?.message || "Could not load the MOU list");
        }
      })
      .catch(() => ToastMsg("error", "Could not load the MOU list"))
      .finally(() => setLoading(false));
  }, [tab, search, owner]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(USERS_SIMPLE_URL).then((res) => {
      const list = res?.data || res?.results || [];
      setUsers(list.map((u) => ({
        value: String(u.id),
        label: [u.login_fname, u.login_lname].filter(Boolean).join(" ").trim() || `#${u.id}`,
      })));
    }).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const openRow = (row) => {
    if (row.id) navigate(`/mou/${row.id}`);
    else setPrepareFor(row);
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      // The response interceptor hands back response.data, so this IS the Blob.
      const data = await apiClient.get(MOU_EXPORT_URL(tab === "not_prepared" ? "" : tab),
                                       { responseType: "blob" });
      const blob = data instanceof Blob ? data : new Blob([data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mou-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      ToastMsg("error", "Could not export");
    } finally {
      setExporting(false);
    }
  };

  const card = "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700";

  return (
    <ContainerCard>
      <div className="space-y-4 p-2">

        {/* ── header ─────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
              <FileSignature className="h-5 w-5 text-primary" />
              MOU
            </h1>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Every won deal and where its memorandum of understanding has got to
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/mou/dashboard")}>
              <BarChart3 className="mr-1 h-4 w-4" /> Dashboard
            </Button>
            <Button variant="outline" size="sm" disabled={exporting} onClick={exportCsv}
                    title="Download the MOUs on this tab as CSV">
              <Download className="mr-1 h-4 w-4" /> Export
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => navigate("/settings/mou")} title="MOU settings">
              <Settings className="mr-1 h-4 w-4" /> Settings
            </Button>
            <Button variant="outline" size="icon" onClick={load} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Whether signing is actually enforced. Without this the screen looks
            like a gate to someone who has not turned it on, and like a
            suggestion to someone who has. */}
        {config.require_mou === false ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              MOU signing is <strong>not required</strong> for this project — clients can be
              onboarded without one. This screen still tracks them.{" "}
              <button className="underline" onClick={() => navigate("/settings/mou")}>
                Turn it on in Settings
              </button>
            </span>
          </div>
        ) : config.require_mou && config.block_onboarding_until_signed === false ? (
          <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-200">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              MOUs are tracked but not enforced — onboarding is not blocked by an unsigned MOU.
            </span>
          </div>
        ) : null}

        {/* ── status tabs ────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700">
          {TABS.map((t) => {
            const count = summary[t.key] ?? 0;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                {t.label}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── filters ────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="w-64 pl-8"
              placeholder="Client or MOU number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select
            placeholder="Owner"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 180 }}
            value={owner || undefined}
            onChange={(v) => setOwner(v || "")}
            options={users}
          />
        </div>

        {/* ── table ──────────────────────────────────────────────── */}
        <div className={`${card} overflow-hidden`}>
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-400">
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500">
              <FileSignature className="h-10 w-10 opacity-30" />
              <p className="text-sm">
                {tab === "open"
                  ? "Nothing waiting on a signature"
                  : "No MOUs in this state"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-700/40">
                    {["Client", "Status", "MOU No", "Value", "Sent", "Signed", "Waiting", "Owner", ""]
                      .map((h, i) => (
                        <th key={i} className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">
                          {h}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((row) => (
                    <tr
                      key={row.id ? `m${row.id}` : `c${row.customer_id}`}
                      className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30"
                      onClick={() => openRow(row)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {row.org_name || "—"}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {[row.city, row.state].filter(Boolean).join(", ") || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status} label={row.status_label} />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                        {row.mou_number || "—"}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                        {fmtCurrency(row.contract_value)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {fmtDate(row.sent_at)}
                        {/* Sent but never opened is the single most useful fact
                            on a chase list, so it is said out loud. */}
                        {row.status === "sent" && !row.viewed_at && (
                          <div className="text-[10px] text-amber-600 dark:text-amber-400">not opened</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {fmtDate(row.signed_at)}
                      </td>
                      <td className="px-4 py-3">
                        {row.status === "signed" || row.status === "cancelled" ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <span className={`inline-flex items-center gap-1 text-xs ${
                            row.age_days > 14
                              ? "font-medium text-red-600 dark:text-red-400"
                              : "text-gray-500 dark:text-gray-400"
                          }`}>
                            <Clock className="h-3 w-3" />
                            {row.age_days ?? 0}d
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {row.owner_name || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {row.status === "not_prepared" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); setPrepareFor(row); }}
                          >
                            <FileSignature className="mr-1 h-3.5 w-3.5" /> Prepare
                          </Button>
                        ) : row.status === "signed" && !row.onboarded_id && row.customer_id ? (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/customers/customerdetails?id=${row.customer_id}`);
                            }}
                          >
                            <UserPlus className="mr-1 h-3.5 w-3.5" /> Onboard
                          </Button>
                        ) : row.onboarded_id ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/onboarded/${row.onboarded_id}`);
                            }}
                          >
                            View client
                          </Button>
                        ) : (
                          <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {prepareFor && (
        <PrepareMOUModal
          customerId={prepareFor.customer_id}
          orgName={prepareFor.org_name}
          users={users}
          onClose={() => setPrepareFor(null)}
          onSuccess={(id) => { setPrepareFor(null); navigate(`/mou/${id}`); }}
        />
      )}
    </ContainerCard>
  );
}
